import { LoadingButton } from "@mui/lab";
import { Box, FormHelperText } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { AuthContextValue } from "../../../contexts/Auth0Context";
import useAuth from "../../../hooks/useAuth";
import useComponentLoader from "../../../hooks/useComponentLoader";
import useMounted from "../../../hooks/useMounted";
import { Auth0Error } from "../../../types/auth0";

const LoginAuth0: FC = (props) => {
  const mounted = useMounted();
  const [loading, toggleLoader] = useComponentLoader();
  const { loginWithPopup, clearCredentials } = useAuth() as AuthContextValue;
  const [error, setError] = useState<string | null>(null);

  // Type guard helper function to safely check the error shape
  function isAuth0Error(unknownError: unknown): unknownError is Auth0Error {
    return (
      typeof unknownError === "object" &&
      unknownError !== null &&
      ("message" in unknownError || "status" in unknownError)
    );
  }

  const handleLogin = async (): Promise<void> => {
    try {
      setError(null); // Clear previous errors
      toggleLoader(true);
      await loginWithPopup();
    } catch (err: unknown) {
      console.error(err);
      clearCredentials();

      if (mounted.current) {
        let errorMessage = "An unexpected error occurred.";
        let isInvalid = false;

        if (isAuth0Error(err)) {
          isInvalid = err.message?.includes("invalid") || err.status === 401;
          errorMessage = err.message || errorMessage;
          setError(errorMessage);
        }

        setError(
          isInvalid
            ? "Invalid username or password. Please try again."
            : errorMessage,
        );
      }
    } finally {
      if (mounted.current) {
        toggleLoader(false);
      }
    }
  };

  return (
    <div {...props}>
      {error && (
        <Box sx={{ my: 3 }}>
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <LoadingButton
          color="primary"
          onClick={handleLogin}
          variant="contained"
          size="large"
          loading={loading}
        >
          Log In
        </LoadingButton>
      </Box>
    </div>
  );
};

export default LoginAuth0;
